
import { computed, defineComponent, PropType } from 'vue'

// components
import TheButton from '@/common/components/UI/TheButton/index.vue'

// types
import { SecurityCompanyInformation } from '@/core/types/security.types'

export default defineComponent({
    components: {
        'the-button': TheButton,
    },
    props: {
        company: {
            type: Object as PropType<SecurityCompanyInformation>,
            required: true,
        },
        isDeclineLoading: {
            type: Boolean,
            default: false,
        },
        isConfirmLoading: {
            type: Boolean,
            default: false,
        },
        isCanConfirm: {
            type: Boolean,
            default: false,
        },
        isCanDecline: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['decline', 'confirm'],
    setup(props, { emit }) {
        const onDecline = () => {
            emit('decline', props.company)
        }

        const onConfirm = () => {
            // const answer = confirm(
            //     `Вы действительно хотите разрешить компании ${
            //         props.company ? props.company.shortName : ''
            //     }?`
            // )
            // if (!answer) return

            emit('confirm', props.company)
        }

        const companyProblems = computed(() => {
            let problems = [] as string[]

            if (props.company.companyRiskFactor.banLeaving) problems.push('Имеется запрет на выезд')
            if (Number(props.company.companyRiskFactor.banLeavingSum))
                problems.push(
                    `Сумма задолженности: ${props.company.companyRiskFactor.banLeavingSum}`
                )
            if (props.company.companyRiskFactor.banNotariusActions)
                problems.push('Запрет на совершение нотариальных действий')
            if (props.company.companyRiskFactor.banRegistrationActionsLegalEnt)
                problems.push('Запрет на регистрационные действия ЮЛ')
            if (props.company.companyRiskFactor.banRegistrationActionsPhysicalEnt)
                problems.push('Запрет на регистрационные действия ФЛ')
            if (props.company.companyRiskFactor.banSeizedPropertyActions)
                problems.push('Запрет на реализацию арестованного имущества')
            if (props.company.companyRiskFactor.bankrupt) problems.push('В списке банкротов')
            if (props.company.companyRiskFactor.enforcementDebt)
                problems.push(
                    `Задолженность по исполнительным производствам: ${props.company.companyRiskFactor.enforcementDebtSum}`
                )
            if (props.company.companyRiskFactor.irresponsibleTaxpayer)
                problems.push(
                    'В списке налогоплательщиков, сделки с которыми совершены без фактического выполнения работ'
                )
            if (props.company.companyRiskFactor.liquidatingTaxpayer)
                problems.push('На стадии ликвидации')
            if (props.company.companyRiskFactor.seizedBankAccount)
                problems.push('Арест на банковский счет')
            if (props.company.companyRiskFactor.seizedProperty) problems.push('Арест на имущество')

            return problems
        })

        const headProblems = computed(() => {
            let problems = [] as string[]

            if (props.company.headRiskFactor.headAlimonyPayer) problems.push('В базе алиментщиков')
            if (props.company.headRiskFactor.headBanLeaving)
                problems.push('Имеется запрет на выезд')
            if (Number(props.company.headRiskFactor.headBanLeavingSum))
                problems.push(
                    `Сумма задолженности: ${props.company.headRiskFactor.headBanLeavingSum}`
                )
            if (props.company.headRiskFactor.headBanNotariusActions)
                problems.push('Запрет на совершение нотариальных действий')
            if (props.company.headRiskFactor.headBanRegistrationActionsLegalEnt)
                problems.push('Запрет на регистрационные действия ЮЛ')
            if (props.company.headRiskFactor.headBanRegistrationActionsPhysicalEnt)
                problems.push('Запрет на регистрационные действия ФЛ')
            if (props.company.headRiskFactor.headBanSeizedPropertyActions)
                problems.push('Запрет на реализацию арестованного имущества')
            if (props.company.headRiskFactor.headCitizenHidingFromInvestigation)
                problems.push('В базе граждан, скрывающихся от следствий')
            if (props.company.headRiskFactor.headDebtorForExecutiveDocuments)
                problems.push('В базе должников по исполнительным документам')
            if (props.company.headRiskFactor.headEnforcementDebt)
                problems.push('Имеются исполнительные производства')
            if (Number(props.company.headRiskFactor.headEnforcementDebtSum))
                problems.push(
                    `Сумма задолженности: ${props.company.headRiskFactor.headEnforcementDebtSum}`
                )
            if (props.company.headRiskFactor.headMissing)
                problems.push('В базе провавших без вести')
            if (props.company.headRiskFactor.headPedophile) problems.push('В базе педофилов')
            if (props.company.headRiskFactor.headSeizedBankAccount)
                problems.push('Арест на банковский счет')
            if (props.company.headRiskFactor.headSeizedProperty) problems.push('Арест на имущество')
            if (Number(props.company.headRiskFactor.headTaxDebt))
                problems.push(
                    `Сумма задолженности по налогам и таможенным платежам: ${props.company.headRiskFactor.headTaxDebt}`
                )
            if (props.company.headRiskFactor.headTerrorist)
                problems.push('Связан с финансированием терроризма')

            return problems
        })

        const headIlligationProblems = computed(() => {
            let problems = [] as string[]

            if (
                props.company.headRiskFactor.headRiskFactorLitigation
                    .headLitigationTotalAdministrativeCount
            )
                problems.push(
                    `Количество административных дел: ${props.company.headRiskFactor.headRiskFactorLitigation.headLitigationTotalAdministrativeCount}`
                )
            if (props.company.headRiskFactor.headRiskFactorLitigation.headLitigationTotalCivilCount)
                problems.push(
                    `Количество гражданских дел: ${props.company.headRiskFactor.headRiskFactorLitigation.headLitigationTotalCivilCount}`
                )
            if (
                props.company.headRiskFactor.headRiskFactorLitigation
                    .headLitigationTotalCriminalCount
            )
                problems.push(
                    `Количество уголовных дел: ${props.company.headRiskFactor.headRiskFactorLitigation.headLitigationTotalCriminalCount}`
                )

            return problems
        })

        const isRenderProblems = computed(
            () =>
                companyProblems.value.length ||
                headProblems.value.length ||
                headIlligationProblems.value.length
        )

        return {
            onDecline,
            onConfirm,
            companyProblems,
            headProblems,
            headIlligationProblems,
            isRenderProblems,
        }
    },
})
